import { loadableReady } from '@loadable/component';
import { hydrateRoot } from 'react-dom/client';
import { historySingleton } from 'src/client/history';
import {
  checkIsAdBlockEnabled,
  disableAdBlockCheck,
} from 'src/common/components/utils/isAdBlockEnabled';
import {
  AUTH_EMAIL_SIGNIN_RECAPTCHA_ENABLED,
  AUTH_EMAIL_SIGNUP_RECAPTCHA_ENABLED,
} from 'src/common/constants/experiments/auth';
import {
  selectExperiment,
  selectIsUnifiedEventsContentReportingEnabled,
} from 'src/common/selectors/config';
import { selectBreadcrumbId } from 'src/common/selectors/reporting';
import { createAppServices, setClientAppServices } from 'src/common/services';
import { loadRecaptchaV3WhenIdle } from 'src/common/utils/auth/recaptcha';
import { isRootPathWithReporting } from 'src/common/utils/guideItemTypes';
import { logClientInfo } from '../common/actions/logging';
import { setBreadcrumbId, setPageLoadId } from '../common/actions/reporting';
import web from '../common/constants/analytics/categoryActionLabel/web';
import getRoutes from '../common/routes';
import { selectIsDiscord } from '../common/selectors/app';
import { initBranch } from '../common/utils/branch/branchHelper';
import initDesktop from '../common/utils/desktop/initDesktop';
import { getActivityIdQueryParam } from '../common/utils/discord';
import removeElementById from '../common/utils/dom/removeElementById';
import { isDevBuild, isLocalEnv } from '../common/utils/environment';
import { updateReportingContext } from '../common/utils/reporting';
import { initClientAnalytics, screenViewTracker, sendPageView } from './analytics';
import { api } from './api';
import { DISCORD_DEVICE_SERIAL } from './constants/localStorage';
import { initAdsTargetingDebug } from './debug/adsTargetingDebug';
import Root from './Root';
import configureClientStore from './store/configureClientStore';
import { storeSingleton } from './store/storeSingleton';
import createTuneInApiClient from './utils/createTuneInApiClient';
import localStorageController from './utils/localStorage';

function getIntialClientVars() {
  const { INITIAL_STATE: initialState, INITIAL_SERVICES: initialServices, L10N } = window;

  delete window.INITIAL_STATE;
  delete window.INITIAL_SERVICES;
  delete window.L10N;
  removeElementById('initialStateEl');
  removeElementById('initialServicesEl');

  initialState.app.isDesktop = initDesktop(initialState.app.isDesktop);

  // HACK: storing/overriding the serial in Local Storage addresses a cookie issue in the Discord app on iOS.
  // Client-side cookie storage was also unreliable. See docs/features/Discord.md for more info.
  if (initialState.app.isDiscord) {
    const { app } = initialState;
    app.tuneInUserSerial = localStorageController.getOrSetDefault(
      DISCORD_DEVICE_SERIAL,
      app.tuneInUserSerial,
    );
  }

  return { initialState, initialServices, L10N };
}

function registerWindowLoadListener(store, services) {
  window.onload = () => {
    // Using a setTimeout here so the browser may consider this `onload` callback as `completed`. By
    // pushing the callback to the event loop, this will simply allow us to measure metrics that can
    // only be measured after all `onload` callbacks are completed (e.g. measuring the loadEventEnd
    // metric, for which https://stackoverflow.com/a/11048634 was recommended).
    setTimeout(async () => {
      const state = store.getState();

      if (selectIsDiscord(state)) {
        disableAdBlockCheck();
        return;
      }

      const isAdBlockEnabled = await checkIsAdBlockEnabled();
      const event = {
        category: web.category,
        action: web.actions.adblocker,
        label: isAdBlockEnabled,
      };

      api.telemetry.reportEvent({ event });
      services.stores.appStore.getState().setHasAdBlocker(isAdBlockEnabled);
    }, 0);
  };
}

function setupDebugger(store, services) {
  if (!isLocalEnv()) {
    return;
  }

  window.tiDebug = {
    api: api.instance,
    store,
    services,
    browserHistory: historySingleton,
    toggleActionLogs() {
      const debugState = store.getState().app.debug;
      // we can mutate state here since this is for debugging purposes only
      debugState.showActionLogs = !debugState.showActionLogs;
    },
  };
}

function makeOnHistoryUpdate(store) {
  return (location) => {
    const storeState = store.getState();

    sendPageView(location);

    if (!selectIsUnifiedEventsContentReportingEnabled(storeState)) {
      return;
    }

    store.dispatch(setPageLoadId());

    if (isRootPathWithReporting(location.pathname) && selectBreadcrumbId(storeState)) {
      store.dispatch(setBreadcrumbId({ reset: true }));
    }
  };
}

function logAppSessionStarted(store, initialState) {
  const isDiscord = selectIsDiscord(initialState);

  api.telemetry.reportApplicationSessionStarted();
  store.dispatch(
    logClientInfo({
      message: 'App session started',
      context: {
        ...(isDiscord && {
          state: { activityId: getActivityIdQueryParam() },
          durationMs: Math.round(performance.now() - window.discordLaunchTiming),
        }),
      },
    }),
  );

  if (isDiscord) {
    window.discordLaunchTiming = performance.now();
  }
}

function logHydrationRecoverableError(error, errorInfo) {
  api.telemetry.logError({
    message: 'React hydration recoverable error',
    context: {
      componentStack: errorInfo?.componentStack,
      digest: errorInfo?.digest,
      error,
      errorCause: error?.cause,
      href: window.location.href,
      pathname: window.location.pathname,
    },
  });
}

function loadRecaptchaInBackground(store) {
  const state = store.getState();

  if (
    selectExperiment(state, AUTH_EMAIL_SIGNIN_RECAPTCHA_ENABLED, false) ||
    selectExperiment(state, AUTH_EMAIL_SIGNUP_RECAPTCHA_ENABLED, false)
  ) {
    loadRecaptchaV3WhenIdle();
  }
}

function hydrateReactApp(store, services, L10N) {
  let numHotReloads = 0;

  function start() {
    hydrateRoot(
      document.getElementById('content'),
      <Root
        onHistoryUpdate={makeOnHistoryUpdate(store)}
        store={store}
        services={services}
        routes={getRoutes()}
        numHotReloads={numHotReloads++}
        {...L10N}
      />,
      {
        onRecoverableError: logHydrationRecoverableError,
      },
    );
    // Scheduled once hydration has started, so the idle wait covers hydration rather than ending
    // while the page's own chunks are still downloading.
    loadRecaptchaInBackground(store);
  }

  if (isDevBuild()) {
    start();

    if (module.hot) {
      // See for context on react-router + HMR:
      // - https://github.com/ReactTraining/react-router/issues/2182#issuecomment-147016657
      // - https://github.com/ReactTraining/react-router/issues/2182#issuecomment-170807654
      // This will be easier when react-router v4 drops https://github.com/ReactTraining/react-router/issues/2182#issuecomment-246901302
      module.hot.accept('../common/routes', start);
    }
  } else {
    // Instruction to wait for all bundles for the page to load before rendering
    // See https://github.com/gregberge/loadable-components
    // NOTE/TODO: depending on how other libraries should load based on page (e.g. @tunein/mint or
    // @tunein/web-tuner), this application may need to be broader.
    // LOADABLE_CHUNK_LOADING_GLOBAL defined at build time. See ./docs/loadable-chunk-loading.md
    loadableReady(start, { chunkLoadingGlobal: LOADABLE_CHUNK_LOADING_GLOBAL });
  }
}

export default function initClient() {
  const { initialState, initialServices, L10N } = getIntialClientVars();
  createTuneInApiClient(initialState);
  const services = createAppServices({ api: api.instance, ...initialServices });
  setClientAppServices(services);
  const store = configureClientStore(initialState, services);

  historySingleton.init();
  storeSingleton.init(store);
  screenViewTracker.init();
  updateReportingContext(store.getState());
  store.subscribe(() => updateReportingContext(store.getState()));
  logAppSessionStarted(store, initialState);
  store.dispatch(setPageLoadId());
  registerWindowLoadListener(store, services);
  setupDebugger(store, services);
  initAdsTargetingDebug();
  initClientAnalytics(store);
  initBranch(store);
  hydrateReactApp(store, services, L10N);
}
